import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { UsersService } from 'src/app/features/users/users.service';
import { UserAvatarComponent } from 'src/app/shared/components/user-avatar/user-avatar.component';
import type { User } from 'src/app/shared/models/users.model';
import { StandaloneDefaultImports } from 'src/app/shared/ui/standalone-default-imports';

@Component({
    selector: 'mention-element',
    imports: [...StandaloneDefaultImports, SkeletonModule, UserAvatarComponent],
    templateUrl: './mention.component.html',
    styleUrls: ['./_mention.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:click)': 'clickout($event)',
        '(document:keydown.escape)': 'onKeydownHandler()'
    }
})
export class MentionComponent {
    // Section: SERVICES / DEPENDENCIES
    //----------------------------------------------------------------------
    public readonly eRef = inject(ElementRef);
    private readonly usersService = inject(UsersService);
    private readonly navigationService = inject(NavigationService);
    private readonly destroyRef = inject(DestroyRef);

    // Section: SIGNALS, OBSERVABLES AND STATE REACTIVE
    //----------------------------------------------------------------------
    id = input<number>();

    public readonly isActive = signal<boolean>(false);
    public readonly userData = signal<User | null>(null);

    // Section: PUBLIC METHODS
    //----------------------------------------------------------------------
    public clickout(event: Event): void {
        if (!this.eRef.nativeElement.contains(event.target as Node)) {
            this.isActive.set(false);
        }
    }

    public onKeydownHandler(): void {
        this.isActive.set(false);
    }

    public showMentionedInfo(): void {
        const userId = this.id();

        if (!this.isActive() && userId) {
            this.isActive.set(true);

            this.usersService
                .findOne(userId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response: User) => {
                        this.userData.set(response);
                    },
                    error: (error: unknown) => {
                        console.error(error);
                    }
                });
        }
    }

    public goMentionedProfile(): void {
        const user = this.userData();
        if (user?.id) {
            this.navigationService.navigateTo(['/user', user.id, 'update', 'companydata']);
        }
    }
}
